import { hasPermission, PermissionInput } from '@app/common/src/permissions';
import { call } from '@everlutionsk/helpers';
import { useBrowserLocation } from '@everlutionsk/ui';
import { NavLink, useNavigate } from '@everlutionsk/ui-router';
import CloseIcon from '@mui/icons-material/Close';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import ManageAccountsOutlinedIcon from '@mui/icons-material/ManageAccountsOutlined';
import NotificationsOutlinedIcon from '@mui/icons-material/NotificationsOutlined';
import ReceiptLongOutlinedIcon from '@mui/icons-material/ReceiptLongOutlined';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import SettingsBackupRestoreOutlinedIcon from '@mui/icons-material/SettingsBackupRestoreOutlined';
import SpaceDashboardOutlinedIcon from '@mui/icons-material/SpaceDashboardOutlined';
import TuneOutlinedIcon from '@mui/icons-material/TuneOutlined';
import WorkHistoryOutlinedIcon from '@mui/icons-material/WorkHistoryOutlined';
import {
  Box,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Stack,
  styled,
  SxProps,
  Theme,
  Tooltip,
  Typography
} from '@mui/material';
import { TFunction } from 'i18next';
import React, { ReactNode, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import qzilaIcon from '../../public/favicon-qzila.png';
import asignIcon from '../../public/icon.svg';
import { Divider } from '../components/coreComponents/divider';
import { environment } from '../environment';
import { useIdentity } from '../identityContext';
import { isDarkMode } from '../theme';

const minNavBarWidth = '80px';
const expandedNavBarWidth = '300px';

const topIconLabel = 'topIcon';

export type NavSection =
  | '/'
  | '/jobs'
  | '/logs'
  | '/recovery'
  | '/pending-actions'
  | '/administrators'
  | '/organisation'
  | `/organisation/${string}`
  | '/business-services'
  | '/business-functions'
  | '/enabling-services'
  | '/assets'
  | '/suppliers'
  | '/customers'
  | '/users'
  | '/roles-and-permissions'
  | '/threats'
  | '/bia'
  | '/risks'
  | '/measures'
  | '/exceptions'
  | '/goals'
  | '/vulnerabilities'
  | '/kpis'
  | '/events'
  | '/interested-parties'
  | '/campaign'
  | '/settings'
  | '/settings/organisation'
  | '/settings/tree'
  | '/settings/frameworks'
  | '/settings/sso'
  | '/settings/team'
  | '/settings/personal-account'
  | '/settings/theme'
  | '/settings/invites'
  | '/settings/functions'
  | '/settings/security'
  | '/settings/security/password'
  | '/settings/language'
  | '/settings/billing'
  | '/compliance'
  | '/policies'
  | '/policies/isms'
  | '/policies/isms/security-policy'
  | '/policies/bcms'
  | '/policies/search'
  | '/rasci'
  | '/access-matrix'
  | '/assessments'
  | '/notifications';

export interface NavigationItem {
  readonly icon: ReactNode;
  readonly label?: string;
  readonly to?: NavSection;
  readonly onClick?: () => void;
  readonly onClose?: () => void;
  readonly slot: 'main' | 'settings';
  readonly isNavBarExpanded?: boolean;
  readonly setIsNavBarExpanded?: (value: boolean) => void;
  readonly isBurgerMenu?: boolean;
  readonly isBurgerOpen?: boolean;
}

interface NavigationChild {
  readonly to: NavSection;
  readonly label: string;
  readonly icon?: ReactNode;
  readonly dividerAfter?: boolean;
  readonly groupLabel?: string;
}

interface ParentSection {
  readonly key: string;
  readonly icon: ReactNode;
  readonly label: string;
  readonly children: NavigationChild[];
  readonly navigateTo?: NavSection;
}

export function getParentSections({
  translation,
  organisationIds,
  identity
}: {
  readonly translation: TFunction;
  readonly organisationIds: string[];
  readonly identity: Pick<ReturnType<typeof useIdentity>, 'organisationRoleActive' | 'role'>;
}): ParentSection[] {
  void organisationIds;

  const defaultSections: ParentSection[] = [
    {
      key: 'main',
      label: topIconLabel,
      icon: (
        <img
          src={call((): string => {
            if (environment.isQzila) return qzilaIcon;
            return asignIcon;
          })}
          alt="Home Icon"
          style={{ width: '36px', height: '36px' }}
        />
      ),
      children: [],
      navigateTo: '/'
    },
    {
      key: 'dashboards',
      label: translation('components.navigation.dashboards'),
      icon: <SpaceDashboardOutlinedIcon />,
      children: [
        // Only the first child carries the groupLabel — the renderer prints
        // it as a section header above this item, and consecutive children
        // in the same group share it. Without the dedupe each item would
        // print its own "MAIN" header.
        {
          to: '/',
          label: translation('components.navigation.accounts'),
          groupLabel: translation('components.navigation.groupMain'),
          icon: <SpaceDashboardOutlinedIcon />
        },
        {
          to: '/jobs',
          label: translation('components.navigation.jobs'),
          icon: <WorkHistoryOutlinedIcon />
        },
        {
          to: '/logs',
          label: 'Logs',
          icon: <ReceiptLongOutlinedIcon />
        },
        {
          to: '/recovery',
          label: translation('components.navigation.recovery'),
          icon: <SettingsBackupRestoreOutlinedIcon />
        },
        {
          to: '/pending-actions',
          label: translation('components.navigation.pendingActions'),
          icon: <PendingActionsOutlinedIcon />
        },
        {
          to: '/administrators',
          label: 'Administrators',
          icon: <ManageAccountsOutlinedIcon />
        },
        // Slack notification settings manage a bot token (a secret), so the
        // entry is superAdmin-only — matching the server-side
        // requireAdminRole(ctx, 'superAdmin') gate on every adminSlack* resolver.
        ...(identity.role === 'superAdmin'
          ? [
              {
                to: '/notifications' as const,
                label: 'Notifications',
                icon: <NotificationsOutlinedIcon />
              }
            ]
          : [])
      ]
    }
  ];

  // Admin interface: only show main navigation
  return defaultSections;
}

export function getSettingsCommon({
  translation,
  identity
}: {
  readonly translation: TFunction;
  readonly identity: Pick<ReturnType<typeof useIdentity>, 'organisationRoleActive'>;
}): NavigationChild[] {
  const personalAccount: NavigationChild = {
    to: '/settings/personal-account',
    label: translation('user.overview.title'),
    groupLabel: translation('components.navigation.groupPersonal')
  };

  const security: NavigationChild = {
    to: '/settings/security',
    label: translation('personalSettings.settingsPersonal.security')
  };

  const themeAndLanguage: NavigationChild[] = [
    { to: '/settings/theme', label: translation('personalSettings.themeSettings.title') },
    { to: '/settings/language', label: translation('personalSettings.settingsPersonal.language') }
  ];

  const isPersonalAccountAccessible = call((): boolean => {
    if (identity.organisationRoleActive == null) return false;
    if (identity.organisationRoleActive.role.value == null) return false;

    return true;
  });

  return [
    ...(isPersonalAccountAccessible ? [personalAccount] : []),
    security,
    ...(!environment.isProd ? themeAndLanguage : [])
  ];
}

function getSettingsOwner({ translation }: { readonly translation: TFunction }): NavigationChild[] {
  const organisationAdministration: NavigationChild[] = [
    {
      to: '/settings/organisation',
      label: translation('settings.organisationSettings.title'),
      groupLabel: translation('components.navigation.groupOrganisationAdministration')
    },
    { to: '/settings/tree', label: translation('settings.settings.sidebar.corporateTree') },
    { to: '/settings/functions', label: translation('settings.settings.sidebar.functions') }
  ];

  const accessAndTeam: NavigationChild[] = [
    {
      to: '/settings/team',
      label: translation('settings.settings.sidebar.teamMembers'),
      groupLabel: translation('components.navigation.groupAccessAndTeam')
    },
    { to: '/settings/invites', label: translation('components.navigation.invites') },
    ...(!environment.isProd
      ? [{ to: '/settings/sso' as const, label: translation('settings.settings.sidebar.sso') }]
      : [])
  ];

  const subscription: NavigationChild[] = [
    {
      to: '/settings/billing',
      label: translation('settings.billing.title'),
      groupLabel: translation('components.navigation.groupSubscription')
    }
  ];

  return [...organisationAdministration, ...accessAndTeam, ...subscription];
}

function settingsSections({
  translation,
  organisationIds,
  identity
}: {
  readonly translation: TFunction;
  readonly isOwner: boolean;
  readonly organisationIds: string[];
  readonly identity: Pick<ReturnType<typeof useIdentity>, 'organisationRoleActive' | 'role'>;
}): ParentSection[] {
  // Bottom-pinned Settings section. Mirrors the main asign nav: a single
  // "Sign-in & security" child where an admin sets/changes their own password.
  // (The richer org/team settings helpers stay unused — admin has no tenant.)
  const settings: ParentSection = {
    key: 'settings',
    label: 'Settings',
    icon: <TuneOutlinedIcon />,
    children: [
      {
        to: '/settings/security/password',
        label: 'Sign-in & security',
        icon: <LockOutlinedIcon />
      }
    ]
  };

  return [...getParentSections({ translation, organisationIds, identity }), settings];
}

interface NavigationBarProps {
  readonly items: NavigationItem[];
  readonly activeSection: string | null;
  readonly setActiveSection: (sectionKey: string | null) => void;
  readonly isNavBarExpanded: boolean;
  readonly setIsNavBarExpanded: (value: boolean) => void;
}

export function NavigationBar(props: NavigationBarProps) {
  const { t: translation } = useTranslation();
  const identity = useIdentity();
  const isOwner = props.items.some(item => item.slot === 'settings');
  const location = useBrowserLocation();

  const permissionFor = (to?: NavSection): PermissionInput | undefined => {
    if (to === '/settings/organisation') return 'settings_organisation.view';
    if (to === '/settings/sso') return 'settings_ssoAndProvisioning.view';
    if (to === '/settings/team') return 'settings_teamMembers.view';
    if (to === '/settings/invites') return 'settings_invites.view';
    if (to === '/settings/functions') return 'settings_functions.view';
    return undefined;
  };

  const ownerItems = useMemo(() => {
    const items = getSettingsOwner({ translation });
    return items.filter(child => {
      const perm = permissionFor(child.to);
      if (!perm) return true;
      return hasPermission(identity.permissions, perm);
    });
  }, [translation, identity.permissions]);

  // Settings is hidden in admin — see settingsSections() — so we don't
  // compute the children or the mobile entries for it.
  void ownerItems;

  const sections = useMemo(
    () =>
      settingsSections({
        translation,
        isOwner,
        organisationIds: identity.organisationRoles.map(role => role.organisation.value),
        identity
      }),
    [translation, isOwner, identity]
  );

  const mobileSettingsItems: NavigationItem[] = [];

  useEffect(() => {
    if (props.activeSection != null) return;

    const sectionsToCheck = sections.filter(section => section.key !== 'main');

    // First, try to find the most specific child match (longest matching path)
    let bestMatch: ParentSection | undefined;
    let bestMatchLength = 0;

    for (const parent of sectionsToCheck) {
      // Check children first (they are more specific)
      for (const child of parent.children) {
        if (location.pathname.startsWith(child.to)) {
          if (child.to.length > bestMatchLength) {
            bestMatch = parent;
            bestMatchLength = child.to.length;
          }
        }
      }

      // Check parent's navigateTo
      if (parent.navigateTo && location.pathname.startsWith(parent.navigateTo)) {
        if (parent.navigateTo.length > bestMatchLength) {
          bestMatch = parent;
          bestMatchLength = parent.navigateTo.length;
        }
      }
    }

    // Fallback to dashboard if no match found
    const defaultActive =
      bestMatch ?? sectionsToCheck.find(s => s.key === 'dashboards') ?? sectionsToCheck[0];

    if (defaultActive) props.setActiveSection(defaultActive.key);
  }, [props.activeSection, location.pathname, sections, props.setActiveSection]);

  return (
    <>
      <NavBarDesktop
        settingsItems={sections}
        activeSection={props.activeSection}
        setActiveSection={props.setActiveSection}
        setIsNavBarExpanded={props.setIsNavBarExpanded}
        isNavBarExpanded={props.isNavBarExpanded}
      />
      <NavBarMobile mainItems={props.items} settingsItems={mobileSettingsItems} />
    </>
  );
}

/**
 * Side navigation bar for the application.
 **/

interface NavBarDesktopProps {
  readonly settingsItems: ParentSection[];
  readonly activeSection: string | null;
  readonly setActiveSection: (sectionKey: string | null) => void;
  readonly isNavBarExpanded: boolean;
  readonly setIsNavBarExpanded: (value: boolean) => void;
}

function NavBarDesktop(props: NavBarDesktopProps) {
  const PANEL_PAD = 7;
  const { t: translation } = useTranslation();
  const navigate = useNavigate();
  const expandedRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (!props.isNavBarExpanded || !expandedRef.current) return;

      const { target } = event;

      if (target instanceof Node && !expandedRef.current.contains(target)) {
        props.setIsNavBarExpanded(false);
        props.setActiveSection(null);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [props.isNavBarExpanded, props.setIsNavBarExpanded, props.setActiveSection]);

  const listItemButtonRefs = useRef<Record<string, HTMLDivElement | null>>({});
  const setListItemButtonRef = (sectionKey: string) => (element: HTMLDivElement | null) => {
    listItemButtonRefs.current[sectionKey] = element;
  };

  const handleSectionClick = (section: ParentSection) => {
    if (section.key === 'main') {
      props.setActiveSection('dashboards');
      props.setIsNavBarExpanded(false);
      navigate(section.navigateTo ?? '/');
      requestAnimationFrame(() => {
        listItemButtonRefs.current.dashboards?.focus?.();
      });
      return;
    }

    if (section.children.length === 1) {
      const [onlyChild] = section.children;
      props.setActiveSection(section.key);
      props.setIsNavBarExpanded(false);
      navigate(onlyChild.to);
      return;
    }

    if (section.children.length > 0) {
      props.setActiveSection(section.key);
      props.setIsNavBarExpanded(true);
      return;
    }
    if (section.navigateTo) {
      props.setActiveSection(section.key);
      props.setIsNavBarExpanded(false);
      navigate(section.navigateTo);
    }
  };

  const currentChildren = useMemo(() => {
    const parent = props.settingsItems.find(parent => parent.key === props.activeSection);
    return parent?.children ?? [];
  }, [props.activeSection, props.settingsItems]);

  const topSections = useMemo(
    () => props.settingsItems.filter(setting => setting.key !== 'settings'),
    [props.settingsItems]
  );
  const settingsSection = useMemo(
    () => props.settingsItems.find(setting => setting.key === 'settings'),
    [props.settingsItems]
  );

  const hasOwnerExtras = currentChildren.some(navigationChild =>
    getSettingsOwner({ translation }).some(
      ownerExtraItem => ownerExtraItem.to === navigationChild.to
    )
  );
  {
    const listItemIconSx: SxProps<Theme> = {
      minWidth: 'auto',
      '& :is(.MuiSvgIcon-root, svg)': {
        color: theme => theme.brandPalette.foreground.neutralFaded,
        fill: 'currentColor',
        transition: 'transform 0.2s ease, color 0.2s ease, fill 0.2s ease, opacity 0.2s ease'
      }
    };

    const listContainerSx: SxProps<Theme> = {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      minHeight: 'calc(100vh - 120px) !important',
      position: 'fixed',
      bgcolor: theme => theme.brandPalette.background.neutral,
      width: minNavBarWidth,
      p: 7,
      top: 0,
      left: 0,
      bottom: 0,
      zIndex: theme => theme.zIndex.appBar,
      borderRight: theme => `1px solid ${theme.brandPalette.border.neutralFaded}`,
      transition: 'width 0.5s ease'
    };

    const listContainerChildSx: SxProps<Theme> = {
      display: 'flex',
      flexDirection: 'column',
      gap: 4,
      ml: `${minNavBarWidth}`,
      bgcolor: theme => theme.brandPalette.background.neutral,
      borderRight: theme => `1px solid ${theme.brandPalette.border.neutralFaded}`,
      transition: 'all 0.3s ease',
      minHeight: 'calc(100vh - 120px) !important',
      position: 'fixed',
      overflow: 'auto',
      p: PANEL_PAD,
      top: 0,
      left: 0,
      bottom: 0,
      zIndex: theme => theme.zIndex.appBar
    };

    const getListItemButtonSx = (isMain: boolean): SxProps<Theme> => ({
      width: 48,
      height: 48,
      borderRadius: theme => theme.radius.md,
      justifyContent: 'center',
      ...(isMain ? { mb: 5 } : { mb: 3 }),
      '&:hover': isMain
        ? {
            backgroundColor: theme => theme.brandPalette.foreground.inverse,
            cursor: 'pointer',
            '& .MuiListItemIcon-root :is(.MuiSvgIcon-root, svg)': {
              transform: 'scale(1.08)',
              opacity: 0.9
            }
          }
        : {
            backgroundColor: theme => theme.brandPalette.background.fadedHover,
            '& .MuiListItemIcon-root :is(.MuiSvgIcon-root, svg)': {
              fill: theme => theme.brandPalette.foreground.neutralFaded,
              transform: 'scale(1.08)',
              opacity: 0.9
            }
          },
      ...(isMain
        ? null
        : {
            '&.Mui-selected': {
              backgroundColor: theme => theme.brandPalette.background.inverse,
              '& .MuiListItemIcon-root :is(.MuiSvgIcon-root, svg)': {
                color: theme => theme.brandPalette.foreground.inverse,
                fill: theme => theme.brandPalette.foreground.inverse,
                transform: 'scale(1.08)',
                opacity: 0.95
              },
              '&:hover': {
                backgroundColor: theme => theme.brandPalette.background.inverse,
                '& .MuiListItemIcon-root :is(.MuiSvgIcon-root, svg)': {
                  fill: theme => theme.brandPalette.foreground.inverse
                }
              }
            }
          })
    });

    return (
      <Box
        component="nav"
        sx={{
          ...navBarDesktopStyle,
          width: props.isNavBarExpanded ? expandedNavBarWidth : minNavBarWidth,
          transition: 'width 0.5s ease, box-shadow 0.3s ease, opacity 0.5s ease'
        }}
      >
        <Stack sx={listContainerSx} direction="column" justifyContent="space-between">
          <List disablePadding>
            {topSections.map(section => {
              const isMain = section.key === 'main';
              const isSelected = props.activeSection === section.key;

              const button = (
                <ListItemButton
                  disableRipple
                  disableTouchRipple
                  key={section.key}
                  selected={!isMain && isSelected}
                  onClick={() => handleSectionClick(section)}
                  sx={getListItemButtonSx(isMain)}
                  ref={setListItemButtonRef(section.key)}
                >
                  <ListItemIcon sx={listItemIconSx}>{section.icon}</ListItemIcon>
                </ListItemButton>
              );

              if (isMain) return button;

              return (
                <Tooltip key={section.key} title={section.label} arrow placement="right">
                  <span>{button}</span>
                </Tooltip>
              );
            })}
          </List>

          <List sx={{ marginTop: 'auto !important' }} disablePadding>
            {settingsSection ? (
              <Tooltip title={settingsSection.label} arrow placement="right">
                <span>
                  <ListItemButton
                    disableRipple
                    disableTouchRipple
                    key={settingsSection.key}
                    selected={props.activeSection === settingsSection.key}
                    onClick={() => handleSectionClick(settingsSection)}
                    sx={getListItemButtonSx(false)}
                  >
                    <ListItemIcon sx={listItemIconSx}>{settingsSection.icon}</ListItemIcon>
                  </ListItemButton>
                </span>
              </Tooltip>
            ) : null}
          </List>
        </Stack>

        {props.isNavBarExpanded && currentChildren.length > 0 && (
          <Box ref={expandedRef} sx={listContainerChildSx}>
            <Typography variant="labelLarge" sx={{ mt: 3 }}>
              {props.settingsItems.find(parent => parent.key === props.activeSection)?.label}
            </Typography>
            <List disablePadding>
              {currentChildren.map((child, index) =>
                child.to ? (
                  <Box key={child.to}>
                    {child.groupLabel != null && (
                      <Typography
                        variant="labelXSmall"
                        sx={{
                          mt: index === 0 ? 3 : 5,
                          mb: 0.5,
                          display: 'block',
                          color: theme => theme.brandPalette.foreground.neutralFaded,
                          textTransform: 'uppercase',
                          letterSpacing: '0.06em',
                          fontSize: '10px'
                        }}
                      >
                        {child.groupLabel}
                      </Typography>
                    )}
                    <NavBarExpandedItem
                      to={child.to}
                      label={child.label}
                      icon={child.icon}
                      useEnd={child.to === '/'}
                    />
                    {child.dividerAfter && hasOwnerExtras ? (
                      <Divider
                        key={`div-${child.to}`}
                        sx={theme => ({
                          my: 2,
                          ml: `-${theme.spacing(PANEL_PAD)}`,
                          mr: `-${theme.spacing(PANEL_PAD)}`
                        })}
                      />
                    ) : null}
                  </Box>
                ) : null
              )}
            </List>
          </Box>
        )}
      </Box>
    );
  }
}

function NavItem({
  icon,
  to,
  onClick,
  label,
  isNavBarExpanded,
  isBurgerMenu,
  isBurgerOpen
}: NavigationItem) {
  const navItemStyles: SxProps<Theme> = {
    p: 0,
    height: '48px',
    width: '48px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    borderRadius: theme => theme.radius.md,
    transition: 'all 0.3s ease-in-out',
    pointerEvents: !isNavBarExpanded ? 'auto' : 'none',

    '&:focus': {
      backgroundColor: theme => theme.brandPalette.background.fadedFocus
    },

    '&:hover': {
      backgroundColor: !isNavBarExpanded
        ? theme => theme.brandPalette.background.fadedHover
        : 'none'
    },
    '&.active': {
      backgroundColor: !isNavBarExpanded
        ? theme => theme.brandPalette.background.secondaryActive
        : 'transparrent'
    },
    '&.active svg': {
      color: !isNavBarExpanded
        ? isDarkMode()
          ? 'black'
          : 'white'
        : theme => theme.brandPalette.foreground.neutralFaded
    },

    ...(isBurgerMenu && {
      '&:hover .burger-lines': {
        transform: 'scale(1.08)'
      }
    }),

    svg: {
      color: theme => theme.brandPalette.foreground.neutralFaded,
      fontSize: '22px'
    }
  };
  if (label === topIconLabel) {
    return (
      <Box
        sx={{
          ...navItemStyles,
          pointerEvents: 'none',
          '&:hover': {
            backgroundColor: 'transparent'
          }
        }}
      >
        <ListItemIcon sx={{ minWidth: 'auto' }}>{icon}</ListItemIcon>
      </Box>
    );
  }

  if (to === undefined) {
    return (
      <ListItem disablePadding sx={{ width: '48px', height: '48px', mb: 2 }} onClick={onClick}>
        <ListItemButton sx={navItemStyles} disableRipple>
          <ListItemIcon sx={{ minWidth: 'auto' }}>
            {isBurgerMenu ? (
              <Box
                className="burger-lines"
                sx={{
                  width: 18,
                  height: 14,
                  position: 'relative',
                  transition: 'transform 0.2s ease',
                  color: theme => theme.brandPalette.foreground.neutralFaded
                }}
              >
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 0,
                    width: '100%',
                    height: 2,
                    borderRadius: '2px',
                    backgroundColor: 'currentColor',
                    transform: isBurgerOpen ? 'translateY(6px) rotate(45deg)' : 'none',
                    transition: 'transform 0.25s ease'
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 6,
                    width: '100%',
                    height: 2,
                    borderRadius: '2px',
                    backgroundColor: 'currentColor',
                    transform: isBurgerOpen ? 'scaleX(0.55)' : 'scaleX(1)',
                    transformOrigin: 'center',
                    opacity: 1,
                    transition: 'transform 0.25s ease'
                  }}
                />
                <Box
                  component="span"
                  sx={{
                    position: 'absolute',
                    left: 0,
                    top: 12,
                    width: '100%',
                    height: 2,
                    borderRadius: '2px',
                    backgroundColor: 'currentColor',
                    transform: isBurgerOpen ? 'translateY(-6px) rotate(-45deg)' : 'none',
                    transition: 'transform 0.25s ease'
                  }}
                />
              </Box>
            ) : (
              icon
            )}
          </ListItemIcon>
        </ListItemButton>
      </ListItem>
    );
  }

  if (to === '/') {
    return (
      <Stack direction="row" alignItems="center">
        {call((): ReactNode => {
          if (isNavBarExpanded)
            return (
              <ListItem
                disablePadding
                sx={{ width: '48px', height: '48px', mb: 2 }}
                onClick={onClick}
              >
                <ListItemButton sx={navItemStyles} disableRipple component={NavLink} to={to} end>
                  <ListItemIcon sx={{ minWidth: 'auto' }}>{icon}</ListItemIcon>
                </ListItemButton>
              </ListItem>
            );

          return (
            <Tooltip title={label} arrow placement="right">
              <ListItem
                disablePadding
                sx={{ width: '48px', height: '48px', mb: 2 }}
                onClick={onClick}
              >
                <ListItemButton sx={navItemStyles} disableRipple component={NavLink} to={to} end>
                  <ListItemIcon sx={{ minWidth: 'auto' }}>{icon}</ListItemIcon>
                </ListItemButton>
              </ListItem>
            </Tooltip>
          );
        })}

        {isNavBarExpanded && <NavBarExpandedItem to={to} label={label} useEnd />}
      </Stack>
    );
  }

  return (
    <Stack direction="row" alignItems="center">
      {call((): ReactNode => {
        if (isNavBarExpanded)
          return (
            <ListItem
              disablePadding
              sx={{ width: '48px', height: '48px', mb: 2 }}
              onClick={onClick}
            >
              <ListItemButton sx={navItemStyles} disableRipple component={NavLink} to={to}>
                <ListItemIcon sx={{ minWidth: 'auto' }}>{icon}</ListItemIcon>
              </ListItemButton>
            </ListItem>
          );

        return (
          <Tooltip title={label} arrow placement="right">
            <ListItem
              disablePadding
              sx={{ width: '48px', height: '48px', mb: 2 }}
              onClick={onClick}
            >
              <ListItemButton sx={navItemStyles} disableRipple component={NavLink} to={to}>
                <ListItemIcon sx={{ minWidth: 'auto' }}>{icon}</ListItemIcon>
              </ListItemButton>
            </ListItem>
          </Tooltip>
        );
      })}

      {isNavBarExpanded && <NavBarExpandedItem to={to} onClick={onClick} label={label} />}
    </Stack>
  );
}

interface NavBarExpandedItemProps {
  readonly label?: string;
  readonly to: NavSection;
  readonly onClick?: () => void;
  readonly useEnd?: boolean;
  readonly icon?: ReactNode;
}

function NavBarExpandedItem({ to, onClick, label, useEnd = false, icon }: NavBarExpandedItemProps) {
  const navItemNavBarExpandedStyles: SxProps<Theme> = {
    display: 'flex',
    justifyContent: 'flex-start',
    fontSize: '0.825rem',
    padding: '8px 12px 8px 0px',
    fontWeight: 500,
    lineHeight: theme => theme.typography.subheadingMedium.lineHeight,
    alignItems: 'center',
    borderRadius: theme => theme.radius.md,
    transition: 'all 0.3s ease-in-out',
    color: theme => theme.brandPalette.foreground.neutralFaded,
    '&:focus': {
      backgroundColor: 'inherit'
    },
    '&:hover, &.active': {
      backgroundColor: 'inherit',
      color: '#4F76FF'
    }
  };
  return (
    <ListItem disablePadding onClick={onClick}>
      <ListItemButton
        sx={navItemNavBarExpandedStyles}
        disableRipple
        component={NavLink}
        to={to}
        {...call(() => {
          if (useEnd) return { end: true };
          return undefined;
        })}
      >
        {icon && (
          <ListItemIcon
            sx={{
              pr: 3,
              minWidth: 0,
              color: 'inherit',
              '& svg': { color: 'inherit' }
            }}
          >
            {icon}
          </ListItemIcon>
        )}
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: '0.825rem',
            lineHeight: 1.25
          }}
        />
      </ListItemButton>
    </ListItem>
  );
}

function NavBarMobile({
  mainItems,
  settingsItems
}: {
  mainItems: NavigationItem[];
  settingsItems: NavigationItem[];
}) {
  const [openMenu, setOpenMenu] = useState<boolean>(false);

  const items = [...mainItems, ...settingsItems];

  return (
    <>
      {openMenu && (
        <NavBarMobileList items={items.slice(4)} onClose={() => setOpenMenu(!openMenu)} />
      )}
      <Stack
        direction="column"
        sx={{
          width: '100%',
          position: 'fixed',
          bottom: '-1px',
          left: '0px',
          right: '0px',
          zIndex: theme => theme.zIndex.appBar + 1,
          borderTop: theme => `1px solid ${theme.brandPalette.border.neutralFaded}`,
          boxShadow: theme => theme.effects.shadows['2xl']
        }}
      >
        <Box component="nav" sx={navBarMobileStyle}>
          <List
            disablePadding
            sx={{
              display: 'flex',
              flexDirection: 'row',
              width: '100%',
              justifyContent: 'space-between'
            }}
          >
            {items.slice(0, 4).map((item, index) => (
              <NavItem key={index} {...item} />
            ))}
            <NavItem
              slot="main"
              icon={<></>}
              isBurgerMenu
              isBurgerOpen={openMenu}
              onClick={() => {
                setOpenMenu(!openMenu);
              }}
            />
          </List>
        </Box>
      </Stack>
    </>
  );
}

function NavBarMobileList({ onClose, items }: { onClose: () => void; items: NavigationItem[] }) {
  return (
    <Box
      sx={{
        position: 'fixed',
        display: 'block',
        top: 0,
        left: 0,
        bottom: 0,
        right: 0,
        zIndex: theme => theme.zIndex.drawer + 50000000,
        backgroundColor: theme => theme.brandPalette.background.neutral
      }}
    >
      <Stack sx={{ height: '100%', width: '100%' }} direction="column">
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ display: { xs: 'flex', md: 'none' }, p: theme => theme.spacing(6) }}
        >
          <Typography variant="labelLarge" sx={{ color: theme => theme.palette.neutral.main }}>
            More
          </Typography>
          <IconButton disableRipple sx={{ p: 0 }} onClick={onClose}>
            <CloseIcon
              sx={{ color: theme => theme.brandPalette.foreground.neutral, fontSize: '24px' }}
            />
          </IconButton>
        </Stack>
        <StyledList>
          {items.map((item, index) => (
            <NavBarMobileListItem key={index} {...item} onClose={onClose} />
          ))}
        </StyledList>
      </Stack>
    </Box>
  );
}

function NavBarMobileListItem({ icon, to = '/', label, onClose }: NavigationItem) {
  return (
    <ListItem disablePadding>
      <ListItemButton
        sx={mobileListItemStyles}
        disableRipple
        component={NavLink}
        to={to}
        end
        onClick={onClose}
      >
        <ListItemIcon
          sx={{
            minWidth: 'auto',
            mr: 4,
            svg: { fontSize: '20px', color: theme => theme.brandPalette.foreground.neutralFaded }
          }}
        >
          {icon}
        </ListItemIcon>
        <Typography
          variant="labelMedium"
          sx={{ color: theme => theme.brandPalette.foreground.neutralFaded }}
        >
          {label}
        </Typography>
      </ListItemButton>
    </ListItem>
  );
}

const navBarDesktopStyle: SxProps<Theme> = {
  minWidth: minNavBarWidth,
  height: 'calc(100vh - 87px)',
  position: 'relative',
  overflowX: 'hidden',
  bgcolor: theme => theme.brandPalette.background.neutral,
  display: { xs: 'none', md: 'flex' }
};

const StyledList = styled(List)(({ theme }) => ({
  background: theme.brandPalette.background.neutral,
  padding: theme.spacing(6, 0)
}));

const navBarMobileStyle: SxProps<Theme> = {
  width: '100%',
  height: 'auto',
  overflow: 'hidden',
  bgcolor: theme => theme.brandPalette.background.neutral,
  px: 6,
  py: 5,
  display: { xs: 'block', md: 'none' },
  zIndex: theme => theme.zIndex.appBar
};

const mobileListItemStyles: SxProps<Theme> = {
  width: '100%',
  p: theme => theme.spacing(3, 5, 3, 5)
};
